import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { fetchAdminContent, saveAdminContent, updateStock } from './adminApi';
import { hasBackend } from '../utils/api';
import { ApiError } from '../core/api/http';
import { errorMessage } from '../core/errors';
import { useSaveParts, type SavePart } from './useSaveParts';
import type { AdminUser } from '../core/types/adminRequests';
import { localCatalog } from '../data/catalog';
import { localSite, siteIssues } from '../data/site';
import { applyFeatures } from '../data/features';
import { pageDefaults, pages as localPages } from '../data/pages';
import { upgradePageContent } from '../core/rules/pageContent';
import { resolveFeatures } from '../core/rules/features';
import { withPolicyDefaults } from '../core/rules/policyDefaults';
import { catalogIssues, createCatalogIndex, type CatalogIndex } from '../utils/catalog';
import { clone } from './editing';
import { undoToast } from './ui/undoToast';
import type { Catalog } from '../core/types/catalog';
import type { SiteSettings } from '../core/types/site';
import type { PageContent } from '../core/types/pages';
import type { Marketing } from '../core/types/marketing';
import type { NewsletterDelivery } from '../core/types/newsletter';
import { brandKit, designs } from './marketing/marketingSeed';
import { socialIdeas, socialPosts } from './social/socialSeed';
import { newsletters } from './newsletter/newsletterSeed';

/**
 * Het concept waar Sarah in werkt. Alles wat ze aanpast, gaat eerst hierin;
 * "Opslaan" stuurt het in één keer naar de backend (PUT /admin/content, zie
 * admin/adminApi.ts), met de versie waarop het gebaseerd is. Voorraad gaat daar niet
 * mee: die slaat StockField meteen apart op (saveStock). Bij het openen wordt de
 * opgeslagen stand geladen; is die er niet, dan begint het bij de lokale data.
 */

export type ListKey = 'products' | 'routines' | 'treatments' | 'articles' | 'concerns';
export type ListItem<K extends ListKey> = Catalog[K][number];
type Item<K extends ListKey> = ListItem<K>;

/**
 * De vlakken die apart "gewijzigd" kunnen zijn. Marketing is één blok naar de server, maar vier
 * vlakken hier (ontwerpen, merkstijl, social, nieuwsbrief), zodat het stipje in het menu bij het juiste scherm staat.
 */
export type AdminArea = ListKey | 'site' | 'pages' | 'designs' | 'brandKit' | 'social' | 'newsletters';
type Draft = {
  catalog: Catalog;
  site: SiteSettings;
  pages: PageContent;
  marketing: Marketing;
};
type AdminValue = {
  /** Wie er is ingelogd. */
  user: AdminUser;
  /** Bezig met het ophalen van de opgeslagen stand. */
  loading: boolean;
  saving: boolean;
  catalog: Catalog;
  site: SiteSettings;
  pages: PageContent;
  marketing: Marketing;
  index: CatalogIndex;
  /** Koppelingen die niet kloppen, live berekend op het concept. */
  issues: string[];
  changed: Record<AdminArea, boolean>;
  dirty: boolean;
  setCatalog: (fn: (c: Catalog) => Catalog) => void;
  setSite: (fn: (s: SiteSettings) => SiteSettings) => void;
  setPages: (fn: (p: PageContent) => PageContent) => void;
  setMarketing: (fn: (m: Marketing) => Marketing) => void;
  putItem: <K extends ListKey>(key: K, item: Item<K>, previousId?: string) => void;
  /**
   * Een deel van één item aanpassen, op de nieuwste stand (functionele update). Veilig voor
   * meerdere wijzigingen achter elkaar en voor een "Ongedaan maken" van seconden later.
   */
  patchItem: <K extends ListKey>(key: K, id: string, patch: Partial<Item<K>>) => void;
  /**
   * Haalt het item (en verwijzingen ernaar) uit het concept en geeft een functie terug die
   * precies dat terugzet: op de oude plek, met de oude koppelingen, zonder latere wijzigingen
   * aan andere items terug te draaien.
   */
  removeItem: (key: ListKey, id: string) => () => void;
  /**
   * Voorraad meteen opslaan, buiten de concept-balk (PATCH /admin/products/{id}/stock).
   * `true` = opgeslagen. Bij `false` staat de actuele voorraad al in het concept.
   */
  saveStock: (productId: string, stock: number | undefined) => Promise<boolean>;
  /** De verzendstand van een nieuwsbrief overnemen uit een serverantwoord (geen wijziging in het concept). */
  applyDelivery: (id: string, delivery: NewsletterDelivery | null) => void;
  save: () => Promise<void>;
  discard: () => void;
  /**
   * Een los serverblok (bijv. de doorverwijzingen) aanhaken aan dezelfde Opslaan-balk. `null`
   * haakt het weer af. Het blok bewaart zelf (eigen PUT); de balk roept alleen `save` aan.
   */
  registerSave: (key: string, part: SavePart | null) => void;
};

/** Behandelingen en artikelen heten naar hun slug; de rest heeft een id. */
const bySlug: Record<ListKey, boolean> = {
  products: false,
  routines: false,
  treatments: true,
  articles: true,
  concerns: false
};
export const itemId = (key: ListKey, item: Item<ListKey>): string => {
  if (bySlug[key]) return 'slug' in item ? item.slug : '';
  return 'id' in item ? item.id : '';
};

/** De vlakken die "gewijzigd" kunnen zijn, in vaste volgorde. */
const areas: readonly AdminArea[] = ['products', 'routines', 'treatments', 'articles', 'concerns', 'site', 'pages', 'designs', 'brandKit', 'social', 'newsletters'];
const initialDraft = (): Draft => ({
  catalog: clone(localCatalog),
  site: clone(localSite()),
  pages: clone(localPages),
  marketing: clone({
    designs,
    brandKit,
    posts: socialPosts,
    ideas: socialIdeas,
    newsletters
  })
});
const snapshot = (d: Draft): Record<AdminArea, string> => ({
  products: JSON.stringify(d.catalog.products),
  routines: JSON.stringify(d.catalog.routines),
  treatments: JSON.stringify(d.catalog.treatments),
  articles: JSON.stringify(d.catalog.articles),
  concerns: JSON.stringify(d.catalog.concerns),
  site: JSON.stringify(d.site),
  pages: JSON.stringify(d.pages),
  designs: JSON.stringify(d.marketing.designs),
  brandKit: JSON.stringify(d.marketing.brandKit),
  social: JSON.stringify({
    posts: d.marketing.posts,
    ideas: d.marketing.ideas
  }),
  // `delivery` is van de server (zoals voorraad): telt nooit als wijziging.
  newsletters: JSON.stringify(d.marketing.newsletters.map(({
    delivery: _d,
    ...n
  }) => n))
});
const AdminContext = createContext<AdminValue | null>(null);

/**
 * Twee smalle contexten naast de grote, voor wie alleen dat nodig heeft (rol, menu, opslaanbalk).
 * Die renderen dan niet mee bij elke toetsaanslag in een editor, alleen als hun eigen waarde verandert.
 */
type AdminSaveValue = Pick<AdminValue, 'dirty' | 'saving' | 'save' | 'discard'>;
const AdminUserContext = createContext<AdminUser | null>(null);
const AdminSaveContext = createContext<AdminSaveValue | null>(null);
export function AdminProvider({
  user,
  children
}: {
  user: AdminUser;
  children: ReactNode;
}) {
  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [saved, setSaved] = useState(() => snapshot(draft));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  // De versie waarop het concept is gebaseerd; gaat mee bij "Opslaan" (409 als hij verouderd is).
  const revisionRef = useRef(0);

  // De opgeslagen stand ophalen (ook in de demo). Lukt dat niet, dan blijft de lokale data staan.
  // Tot dit klaar is toont de provider niets bewerkbaars, zodat er geen wijziging stil vervangen wordt.
  useEffect(() => {
    let alive = true;
    fetchAdminContent().then(content => {
      if (!alive || !content) return;
      const {
        revision,
        ...stored
      } = content;
      // Oudere opslag (bijv. redenen bij stoppen als lijst) eerst in de vorm van nu.
      // Onderdelen altijd compleet: een oudere opslag kent de nieuwe schakelaars nog niet (= aan).
      const rest = {
        ...stored,
        // Nieuwe instellingen (bijv. kuurValidMonths) die de opslag nog niet kent: de standaard.
        site: {
          ...stored.site,
          policy: withPolicyDefaults(stored.site.policy, localSite().policy),
          features: resolveFeatures(stored.site.features)
        },
        // Met de standaard erbij: nieuwe beeldvelden en blokken staan meteen in Paginateksten.
        pages: upgradePageContent(stored.pages, pageDefaults)
      };
      revisionRef.current = revision;
      setDraft(rest);
      setSaved(snapshot(rest));
    }).catch(() => toast.error('De opgeslagen gegevens konden niet worden geladen', {
      description: 'Je ziet de laatst bekende versie. Ververs de pagina om het opnieuw te proberen.'
    })).finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // Onderdelen (fotodagboek, abonnement) volgen de opgeslagen stand, niet het concept: een schakelaar
  // die nog niet is opgeslagen, verbergt nog niets in het dossier.
  useEffect(() => {
    applyFeatures((JSON.parse(saved.site) as SiteSettings).features);
  }, [saved.site]);
  const setCatalog = useCallback((fn: (c: Catalog) => Catalog) => setDraft(d => ({
    ...d,
    catalog: fn(d.catalog)
  })), []);
  const setSite = useCallback((fn: (s: SiteSettings) => SiteSettings) => setDraft(d => ({
    ...d,
    site: fn(d.site)
  })), []);
  const setPages = useCallback((fn: (p: PageContent) => PageContent) => setDraft(d => ({
    ...d,
    pages: fn(d.pages)
  })), []);
  const setMarketing = useCallback((fn: (m: Marketing) => Marketing) => setDraft(d => ({
    ...d,
    marketing: fn(d.marketing)
  })), []);
  const putItem = useCallback(<K extends ListKey,>(key: K, item: Item<K>, previousId?: string) => setCatalog(c => {
    const list = c[key] as Item<K>[];
    const match = previousId ?? itemId(key, item);
    const i = list.findIndex(x => itemId(key, x) === match);
    const next = i >= 0 ? list.map((x, j) => j === i ? item : x) : [item, ...list];
    return {
      ...c,
      [key]: next
    };
  }), [setCatalog]);
  const patchItem = useCallback(<K extends ListKey,>(key: K, id: string, patch: Partial<Item<K>>) => setCatalog(c => {
    const list = c[key] as Item<K>[];
    const i = list.findIndex(x => itemId(key, x) === id);
    if (i < 0) return c;
    return {
      ...c,
      [key]: list.map((x, j) => j === i ? {
        ...x,
        ...patch
      } as Item<K> : x)
    };
  }), [setCatalog]);

  // De nieuwste stand voor removeItem en discard: wat er weggaat, moet het zijn zoals het nu is.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const removeItem = useCallback((key: ListKey, id: string) => {
    const c = draftRef.current.catalog;
    const list = c[key] as Item<ListKey>[];
    const at = list.findIndex(x => itemId(key, x) === id);
    if (at < 0) return () => {};
    const removed = list[at];
    // Waar het product in stond, met de positie: zo komt het bij herstellen op dezelfde plek terug.
    const where = <T,>(items: T[], ids: (x: T) => string[] | undefined, ownerOf: (x: T) => string) => items.flatMap(x => {
      const i = ids(x)?.indexOf(id) ?? -1;
      return i >= 0 ? [{
        owner: ownerOf(x),
        i
      }] : [];
    });
    const refs = key === 'products' ? {
      routines: where(c.routines, r => r.productIds, r => r.id),
      pairs: where(c.products, p => p.pairs, p => p.id),
      articles: where(c.articles, a => a.productIds, a => a.slug)
    } : null;
    setCatalog(cur => {
      const next = {
        ...cur,
        [key]: (cur[key] as Item<ListKey>[]).filter(x => itemId(key, x) !== id)
      } as Catalog;
      // Een verwijderd product mag nergens meer naar verwijzen.
      if (key === 'products') {
        next.routines = next.routines.map(r => ({
          ...r,
          productIds: r.productIds.filter(p => p !== id)
        }));
        next.products = next.products.map(p => ({
          ...p,
          pairs: p.pairs.filter(x => x !== id)
        }));
        next.articles = next.articles.map(a => ({
          ...a,
          productIds: a.productIds?.filter(x => x !== id)
        }));
      }
      return next;
    });
    return () => setCatalog(cur => {
      const curList = cur[key] as Item<ListKey>[];
      // Intussen opnieuw aangemaakt met dezelfde id: niets overschrijven.
      if (curList.some(x => itemId(key, x) === id)) return cur;
      const restored = [...curList];
      restored.splice(Math.min(at, restored.length), 0, removed);
      const next = {
        ...cur,
        [key]: restored
      } as Catalog;
      if (refs) {
        const back = (ids: string[], owner: string, hits: {
          owner: string;
          i: number;
        }[]) => {
          const hit = hits.find(h => h.owner === owner);
          if (!hit || ids.includes(id)) return ids;
          return [...ids.slice(0, hit.i), id, ...ids.slice(hit.i)];
        };
        next.routines = next.routines.map(r => ({
          ...r,
          productIds: back(r.productIds, r.id, refs.routines)
        }));
        next.products = next.products.map(p => ({
          ...p,
          pairs: back(p.pairs, p.id, refs.pairs)
        }));
        next.articles = next.articles.map(a => a.productIds ? {
          ...a,
          productIds: back(a.productIds, a.slug, refs.articles)
        } : a);
      }
      return next;
    });
  }, [setCatalog]);
  const current = useMemo(() => snapshot(draft), [draft]);
  const changed = useMemo(() => {
    const out = {} as Record<AdminArea, boolean>;
    for (const area of areas) out[area] = current[area] !== saved[area];
    return out;
  }, [current, saved]);
  const draftDirty = Object.values(changed).some(Boolean);
  const index = useMemo(() => createCatalogIndex(draft.catalog), [draft.catalog]);
  const issues = useMemo(() => [...catalogIssues(index), ...siteIssues(draft.site, id => Boolean(index.productById(id)))], [index, draft.site]);

  /** `true` = het beheer-concept staat op de server. */
  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      const {
        revision,
        ...stored
      } = await saveAdminContent({
        ...draft,
        revision: revisionRef.current
      });
      revisionRef.current = revision;
      // Voorraad komt van de server (die negeert wat er in het blok stond): die stand overnemen.
      const stockById = new Map(stored.catalog.products.map(p => [p.id, p.stock]));
      // Net als voorraad: de verzendstand van de nieuwsbrieven komt van de server.
      const deliveryById = new Map(stored.marketing.newsletters.map(n => [n.id, n.delivery ?? null]));
      setDraft(d => ({
        ...d,
        catalog: {
          ...d.catalog,
          products: d.catalog.products.map(p => stockById.has(p.id) ? {
            ...p,
            stock: stockById.get(p.id)
          } : p)
        },
        marketing: {
          ...d.marketing,
          newsletters: d.marketing.newsletters.map(n => ({
            ...n,
            delivery: deliveryById.get(n.id) ?? null
          }))
        }
      }));
      setSaved(snapshot(stored));
      toast.success('Wijzigingen opgeslagen', {
        description: hasBackend ? 'Staat binnen een minuut op de website.' : 'Demo: bewaard in deze browser en na verversen terug in het beheer. De webshop toont nog de lokale data.'
      });
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'version_conflict') {
        // Niet stil overschrijven: Sarah beslist. Het concept blijft staan tot ze ververst.
        toast.error('Het beheer is intussen ergens anders opgeslagen', {
          description: 'Bijvoorbeeld op je telefoon of in een ander tabblad. Ververs de pagina om die versie te zien; je wijzigingen van hier gaan dan verloren, dus noteer even wat je wilt houden.',
          duration: Infinity,
          action: {
            label: 'Verversen',
            onClick: () => window.location.reload()
          }
        });
      } else {
        toast.error('Opslaan lukte niet', {
          description: errorMessage(err) ?? 'Probeer het zo nog eens. Je wijzigingen staan nog klaar.'
        });
      }
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [draft]);
  const saveStock = useCallback(async (productId: string, stock: number | undefined) => {
    // Wat Sarah op haar scherm zag, uit de opgeslagen stand (niet het concept).
    const savedProducts = JSON.parse(saved.products) as Catalog['products'];
    const savedProduct = savedProducts.find(p => p.id === productId);
    // Nieuw product, nog niet opgeslagen: de voorraad gaat als beginvoorraad mee met "Opslaan".
    if (!savedProduct) {
      setDraft(d => ({
        ...d,
        catalog: {
          ...d.catalog,
          products: d.catalog.products.map(p => p.id === productId ? {
            ...p,
            stock
          } : p)
        }
      }));
      return true;
    }
    const expected = savedProduct.stock ?? null;
    // In concept én opgeslagen stand zetten, zodat voorraad nooit "niet opgeslagen" lijkt.
    const apply = (value: number | undefined) => {
      setDraft(d => ({
        ...d,
        catalog: {
          ...d.catalog,
          products: d.catalog.products.map(p => p.id === productId ? {
            ...p,
            stock: value
          } : p)
        }
      }));
      setSaved(s => ({
        ...s,
        products: JSON.stringify(savedProducts.map(p => p.id === productId ? {
          ...p,
          stock: value
        } : p))
      }));
    };
    try {
      const res = await updateStock(productId, stock ?? null, expected);
      apply(res.stock ?? undefined);
      if (!res.applied) {
        toast('Er is intussen iets verkocht', {
          description: res.stock === null ? 'De voorraad wordt nu niet bijgehouden. Kijk even of dat klopt.' : `De voorraad is nu ${res.stock}. Pas hem opnieuw aan als dat niet klopt.`
        });
        return false;
      }
      toast.success('Voorraad opgeslagen', {
        description: 'Staat meteen zo op de website.'
      });
      return true;
    } catch (err) {
      toast.error('Voorraad opslaan lukte niet', {
        description: errorMessage(err) ?? 'Probeer het zo nog eens.'
      });
      return false;
    }
  }, [saved.products]);
  const applyDelivery = useCallback((id: string, delivery: NewsletterDelivery | null) => setDraft(d => ({
    ...d,
    marketing: {
      ...d.marketing,
      newsletters: d.marketing.newsletters.map(n => n.id === id ? {
        ...n,
        delivery
      } : n)
    }
  })), []);
  const discardDraft = useCallback(() => {
    // "Verwerpen" wist soms een uur werk: de stand van vlak ervoor blijft even terug te halen.
    const before = draftRef.current;
    setDraft(d => {
      const restore = (key: AdminArea) => JSON.parse(saved[key]);
      return {
        catalog: {
          ...d.catalog,
          products: restore('products'),
          routines: restore('routines'),
          treatments: restore('treatments'),
          articles: restore('articles'),
          concerns: restore('concerns')
        },
        site: restore('site'),
        pages: restore('pages'),
        marketing: {
          designs: restore('designs'),
          brandKit: restore('brandKit'),
          ...(restore('social') as Pick<Marketing, 'posts' | 'ideas'>),
          // De verzendstand blijft: die staat al op de server, verwerpen draait hem niet terug.
          newsletters: (restore('newsletters') as Marketing['newsletters']).map(n => ({
            ...n,
            delivery: d.marketing.newsletters.find(x => x.id === n.id)?.delivery ?? null
          }))
        }
      };
    });
    undoToast('Wijzigingen verworpen', () => setDraft(before), {
      restored: 'Je wijzigingen staan er weer'
    });
  }, [saved]);

  // Eén Opslaan voor het concept en losse serverblokken (registerSave).
  const {
    registerSave,
    dirty,
    saving: partsSaving,
    save,
    discard
  } = useSaveParts({
    dirty: draftDirty,
    save: saveDraft,
    discard: discardDraft
  });
  const isSaving = saving || partsSaving;
  // Eén object per echte wijziging, niet per render: anders rendert elke `useAdmin()` mee bij
  // elke render van de provider, ook als er niets veranderde.
  const value = useMemo<AdminValue>(() => ({
    user,
    loading,
    saving: isSaving,
    catalog: draft.catalog,
    site: draft.site,
    pages: draft.pages,
    marketing: draft.marketing,
    index,
    issues,
    changed,
    dirty,
    setCatalog,
    setSite,
    setPages,
    setMarketing,
    putItem,
    patchItem,
    removeItem,
    saveStock,
    applyDelivery,
    save,
    discard,
    registerSave
  }), [user, loading, isSaving, draft, index, issues, changed, dirty, setCatalog, setSite, setPages, setMarketing, putItem, patchItem, removeItem, saveStock, applyDelivery, save, discard, registerSave]);
  const saveValue = useMemo<AdminSaveValue>(() => ({
    dirty,
    saving: isSaving,
    save,
    discard
  }), [dirty, isSaving, save, discard]);
  if (loading) {
    return <div role="status" className="admin-root flex min-h-screen min-h-dvh w-full items-center justify-center bg-sand text-[14px] text-muted" data-id="element-115" data-mp="116o73h9td72d">
        Beheer laden…
      </div>;
  }
  return <AdminUserContext.Provider value={user} data-id="element-116" data-mp="3lwvm320avr89">
      <AdminSaveContext.Provider value={saveValue} data-id="element-117" data-mp="3m0b5gh10bmfh">
        <AdminContext.Provider value={value} data-id="element-118" data-mp="3q0l75hhrnqoj">{children}</AdminContext.Provider>
      </AdminSaveContext.Provider>
    </AdminUserContext.Provider>;
}

/** Alles van het beheer. Rendert mee met elke wijziging in het concept. */
export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin moet binnen <AdminProvider> gebruikt worden');
  return ctx;
}

/** Alleen wie er is ingelogd. Verandert niet tijdens het bewerken. */
export function useAdminUser() {
  const ctx = useContext(AdminUserContext);
  if (!ctx) throw new Error('useAdminUser moet binnen <AdminProvider> gebruikt worden');
  return ctx;
}

/** Alleen de opslaanstand en -acties, voor de opslaanbalk. */
export function useAdminSave() {
  const ctx = useContext(AdminSaveContext);
  if (!ctx) throw new Error('useAdminSave moet binnen <AdminProvider> gebruikt worden');
  return ctx;
}